import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { Customer } from "@/components/customers/types";

/** Server pagination meta for the customers list (from the API envelope). */
export interface CustomersPagination {
  count: number;
  totalPages: number;
  currentPage: number;
}

/** The list's current query context — replayed by a post-mutation refresh. */
export interface CustomersListQuery {
  filters?: Record<string, string>;
  page: number;
  pageSize?: number;
}

export interface CustomersState {
  customers: Customer[];
  /** Server pagination meta — the table renders exactly one server page. */
  pagination: CustomersPagination;
  drafts: Customer[];
  loading: boolean;
  draftsLoading: boolean;
  addOpen: boolean;
  draftOpen: boolean;
  deleteCustomerId: string | null;
  /**
   * The filter/page/pageSize of the last list fetch. Kept in the shared store
   * (not component state) so a mutation refresh fired from a drawer — a separate
   * `useCustomers()` instance that never saw the list's filters — can replay the
   * exact current view instead of snapping back to the unfiltered first page.
   */
  listQuery: CustomersListQuery;
}

const EMPTY_PAGINATION: CustomersPagination = {
  count: 0,
  totalPages: 1,
  currentPage: 1,
};

const initialState: CustomersState = {
  customers: [],
  pagination: EMPTY_PAGINATION,
  drafts: [],
  loading: true,
  draftsLoading: true,
  addOpen: false,
  draftOpen: false,
  deleteCustomerId: null,
  listQuery: { page: 1 },
};

const customersSlice = createSlice({
  name: "customers",
  initialState,
  reducers: {
    /** Replace the list with one server page (SSR hydration / filter re-fetch). */
    setCustomers(
      state,
      action: PayloadAction<{
        rows: Customer[];
        count: number;
        totalPages: number;
        currentPage: number;
      }>,
    ) {
      state.customers = action.payload.rows;
      state.pagination = {
        count: action.payload.count,
        totalPages: action.payload.totalPages,
        currentPage: action.payload.currentPage,
      };
    },
    setDraftCustomers(state, action: PayloadAction<Customer[]>) {
      state.drafts = action.payload;
    },
    /** Record the current list query so a post-mutation refresh can replay it. */
    setListQuery(state, action: PayloadAction<CustomersListQuery>) {
      state.listQuery = action.payload;
    },
    setCustomersLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setDraftsLoading(state, action: PayloadAction<boolean>) {
      state.draftsLoading = action.payload;
    },
    /**
     * Reset both lists to their loading state — dispatched when leaving the
     * route so a re-entry shows the loading skeleton instead of flashing the
     * previous visit's persisted (stale) lists before the fresh SSR reads
     * re-hydrate. The Redux store survives navigation.
     */
    resetCustomerLists(state) {
      state.customers = [];
      state.pagination = EMPTY_PAGINATION;
      state.drafts = [];
      state.loading = true;
      state.draftsLoading = true;
      state.listQuery = { page: 1 };
    },
    openAddCustomerDrawer(state) {
      state.addOpen = true;
    },
    closeAddCustomerDrawer(state) {
      state.addOpen = false;
    },
    openDraftCustomerDrawer(state) {
      state.draftOpen = true;
    },
    closeDraftCustomerDrawer(state) {
      state.draftOpen = false;
    },
    requestDeleteCustomer(state, action: PayloadAction<string>) {
      state.deleteCustomerId = action.payload;
    },
    clearDeleteCustomer(state) {
      state.deleteCustomerId = null;
    },
    removeCustomerOptimistic(state, action: PayloadAction<string>) {
      const before = state.customers.length;
      state.customers = state.customers.filter((c) => c.id !== action.payload);
      if (state.customers.length < before) {
        state.pagination.count = Math.max(0, state.pagination.count - 1);
      }
      state.drafts = state.drafts.filter((c) => c.id !== action.payload);
      state.deleteCustomerId = null;
    },
  },
});

export const {
  setCustomers,
  setDraftCustomers,
  setListQuery,
  setCustomersLoading,
  setDraftsLoading,
  resetCustomerLists,
  openAddCustomerDrawer,
  closeAddCustomerDrawer,
  openDraftCustomerDrawer,
  closeDraftCustomerDrawer,
  requestDeleteCustomer,
  clearDeleteCustomer,
  removeCustomerOptimistic,
} = customersSlice.actions;

export default customersSlice.reducer;
